// =============================================================================
// Origin UI — src/pages/admin/CorporateCardAdminTab.tsx
// Admin Config → Credit Cards → Cards tab.
// CC-11.1 v3
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import {
  getCards, createCard, updateCard, deleteCard,
  type CorporateCardListItem, type CreateCorporateCardDto, type UpdateCorporateCardDto
} from '@/services/creditCardApi';
import styles from './admin.module.css';

interface EmployeeOption { employeeId: string; fullName: string; employeeNumber: string; }

const ISSUERS = ['', 'ENBD', 'DBS', 'JPM', 'RABO'];
const CURRENCIES = ['AED', 'SGD', 'USD', 'GBP', 'EUR', 'CNY'];

export function CorporateCardAdminTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cards, setCards] = useState<CorporateCardListItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [editing, setEditing] = useState<CorporateCardListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const editRef = React.useRef<HTMLDivElement>(null);
  const [form, setForm] = useState({
    employeeId: '', lastFourDigits: '', cardIssuer: '', cardCurrency: 'AED',
    costCentreCode: '', creditLimit: '', isActive: true, cardNameOnStatement: ''
  });

  // Sort
  const [sortKey, setSortKey] = useState<string>('employeeName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterIssuer, setFilterIssuer] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterApprover, setFilterApprover] = useState('');
  const [filterActive, setFilterActive] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [c, e] = await Promise.all([getCards(), api.get<EmployeeOption[]>('/api/employees?minimal=true')]);
      setCards(c);
      setEmployees(e.map(emp => ({ ...emp, fullName: (((emp as any).firstName || '') + ' ' + ((emp as any).lastName || '')).trim() || emp.fullName || '' })).filter(emp => emp.fullName.trim() !== '').sort((a, b) => a.fullName.localeCompare(b.fullName)));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  const filteredCards = useMemo(() => {
    let data = cards;
    if (filterEmployee) data = data.filter(c => (c.employeeName || '').toLowerCase().includes(filterEmployee.toLowerCase()));
    if (filterIssuer) data = data.filter(c => c.cardIssuer === filterIssuer);
    if (filterCurrency) data = data.filter(c => c.cardCurrency === filterCurrency);
    if (filterApprover) data = data.filter(c => (c.approverName || '').toLowerCase().includes(filterApprover.toLowerCase()));
    if (filterActive === 'yes') data = data.filter(c => c.isActive);
    if (filterActive === 'no') data = data.filter(c => !c.isActive);

    return [...data].sort((a, b) => {
      let cmp = 0;
      const av = String((a as any)[sortKey] ?? '');
      const bv = String((b as any)[sortKey] ?? '');
      if (sortKey === 'isActive') {
        cmp = (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0);
      } else {
        cmp = av.localeCompare(bv);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [cards, filterEmployee, filterIssuer, filterCurrency, filterApprover, filterActive, sortKey, sortDir]);

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }
  function sIcon(key: string) { return sortKey !== key ? ' \u21D5' : sortDir === 'asc' ? ' \u2191' : ' \u2193'; }

  const hasFilters = filterEmployee || filterIssuer || filterCurrency || filterApprover || filterActive;
  function clearFilters() { setFilterEmployee(''); setFilterIssuer(''); setFilterCurrency(''); setFilterApprover(''); setFilterActive(''); }

  const issuerOptions = useMemo(() => [...new Set(cards.map(c => c.cardIssuer).filter(x => x != null && x !== ''))].sort(), [cards]);
  const currencyOptions = useMemo(() => [...new Set(cards.map(c => c.cardCurrency).filter(x => x != null && x !== ''))].sort(), [cards]);
  const approverList = useMemo(() => [...new Set(cards.map(c => c.approverName).filter(x => x != null && x !== ''))].sort() as string[], [cards]);

  function startCreate() {
    setForm({ employeeId: '', lastFourDigits: '', cardIssuer: '', cardCurrency: 'AED', costCentreCode: '', creditLimit: '', isActive: true, cardNameOnStatement: '' });
    setCreating(true);
    setEditing(null);
  }

  function startEdit(card: CorporateCardListItem) {
    setForm({
      employeeId: card.employeeId,
      lastFourDigits: card.lastFourDigits,
      cardIssuer: card.cardIssuer,
      cardCurrency: card.cardCurrency,
      costCentreCode: card.costCentreCode || '',
      creditLimit: card.creditLimit?.toString() || '',
      isActive: card.isActive,
      cardNameOnStatement: card.cardNameOnStatement || '',
    });
    setEditing(card);
    setCreating(false);
    setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  }

  async function save() {
    try {
      setSaving(true);
      setError('');
      if (editing) {
        const dto: UpdateCorporateCardDto = {
          lastFourDigits: form.lastFourDigits,
          cardIssuer: form.cardIssuer,
          cardCurrency: form.cardCurrency,
          costCentreCode: form.costCentreCode || undefined,
          creditLimit: form.creditLimit ? Number(form.creditLimit) : undefined,
          isActive: form.isActive,
          cardNameOnStatement: form.cardNameOnStatement || undefined,
        };
        await updateCard(editing.corporateCardId, dto);
      } else {
        const dto: CreateCorporateCardDto = {
          employeeId: form.employeeId,
          lastFourDigits: form.lastFourDigits,
          cardIssuer: form.cardIssuer,
          cardCurrency: form.cardCurrency,
          costCentreCode: form.costCentreCode || undefined,
          creditLimit: form.creditLimit ? Number(form.creditLimit) : undefined,
          isActive: form.isActive,
          cardNameOnStatement: form.cardNameOnStatement || undefined,
        };
        await createCard(dto);
      }
      setCreating(false);
      setEditing(null);
      await load();
    } catch (e: any) {
      const b = e.body ? JSON.parse(e.body) : null;
      setError(b?.error || e.message);
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await deleteCard(deleteId); setDeleteId(null); await load(); }
    catch (e: any) { setError(e.message); }
  }

  const selectedEmpName = employees.find(e => e.employeeId === form.employeeId)?.fullName;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 className={styles.formTitle}>
          Corporate Card Register
          {hasFilters && <span style={{ fontSize: 12, color: 'var(--turquoise)', marginLeft: 8 }}>({filteredCards.length} of {cards.length})</span>}
        </h4>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasFilters && <button onClick={clearFilters} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear</button>}
          <button onClick={() => setShowFilters(!showFilters)} style={{ fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, cursor: 'pointer', background: showFilters ? 'var(--turquoise-light)' : 'var(--gray-100)', color: showFilters ? 'var(--turquoise-dark)' : 'var(--gray-600)', border: '1px solid ' + (showFilters ? 'var(--turquoise)' : 'var(--gray-200)') }}>Filters</button>
          <button className={styles.saveBtn} onClick={startCreate}>+ Add Card</button>
        </div>
      </div>

      {showFilters && (
        <div style={{ marginBottom: 12 }}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Employee</label>
              <input className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} placeholder="Search..." value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Issuer</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterIssuer} onChange={e => setFilterIssuer(e.target.value)}>
                <option value="">All</option>{issuerOptions.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Currency</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)}>
                <option value="">All</option>{currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Approver</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterApprover} onChange={e => setFilterApprover(e.target.value)}>
                <option value="">All</option>{approverList.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Active</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterActive} onChange={e => setFilterActive(e.target.value)}>
                <option value="">All</option><option value="yes">Active</option><option value="no">Inactive</option>
              </select>
            </div>
          </div>
        </Card>
        </div>
      )}

      {(creating || editing) && (
        <div ref={editRef} style={{ marginBottom: 16 }}>
        <Card className={styles.formCard} padding={true}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Card' : 'Add Card'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.fieldLabel}>
                Employee
                {selectedEmpName && <span style={{ marginLeft: 8, color: 'var(--green)', fontSize: 13 }}>{'\u2705'} {selectedEmpName}</span>}
              </label>
              <select className={styles.fieldInput} value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))} disabled={!!editing}>
                <option value="">Select employee...</option>
                {employees.map(emp => <option key={emp.employeeId} value={emp.employeeId}>{emp.fullName} ({emp.employeeNumber})</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Last 4 Digits</label>
              <input className={styles.fieldInput} value={form.lastFourDigits} onChange={e => setForm(f => ({ ...f, lastFourDigits: e.target.value }))} maxLength={4} placeholder="e.g. 2699" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Card Issuer</label>
              <select className={styles.fieldInput} value={form.cardIssuer} onChange={e => setForm(f => ({ ...f, cardIssuer: e.target.value }))}>
                {ISSUERS.map(i => <option key={i} value={i}>{i || '(Not specified)'}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Currency</label>
              <select className={styles.fieldInput} value={form.cardCurrency} onChange={e => setForm(f => ({ ...f, cardCurrency: e.target.value }))}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Cost Centre</label>
              <input className={styles.fieldInput} value={form.costCentreCode} onChange={e => setForm(f => ({ ...f, costCentreCode: e.target.value }))} placeholder="e.g. IT, FIN" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Credit Limit</label>
              <input className={styles.fieldInput} type="number" value={form.creditLimit} onChange={e => setForm(f => ({ ...f, creditLimit: e.target.value }))} placeholder="Optional" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Name on Statement</label>
              <input className={styles.fieldInput} value={form.cardNameOnStatement} onChange={e => setForm(f => ({ ...f, cardNameOnStatement: e.target.value }))} placeholder="For matching (e.g. MARK JAMES SMITH)" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Status</label>
              <select className={styles.fieldInput} value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'active' }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setCreating(false); setEditing(null); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={saving || !form.employeeId}>
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Card'}
            </button>
          </div>
        </Card>
        </div>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('employeeName')}>Employee{sIcon('employeeName')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('lastFourDigits')}>Last 4{sIcon('lastFourDigits')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardIssuer')}>Issuer{sIcon('cardIssuer')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardCurrency')}>Currency{sIcon('cardCurrency')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('costCentreCode')}>Cost Centre{sIcon('costCentreCode')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('approverName')}>Approver{sIcon('approverName')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('isActive')}>Active{sIcon('isActive')}</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCards.map(c => (
              <tr key={c.corporateCardId}>
                <td style={{ fontWeight: 600 }}>{c.employeeName} {c.employeeId && <span style={{ color: 'var(--green)', fontSize: 11 }}>✅</span>}</td>
                <td>{c.lastFourDigits || '\u2014'}</td>
                <td>{c.cardIssuer || '\u2014'}</td>
                <td>{c.cardCurrency}</td>
                <td>{c.costCentreCode || '\u2014'}</td>
                <td>{c.approverName || <span style={{ color: 'var(--orange)' }}>Not assigned</span>}</td>
                <td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.isActive ? 'var(--green-light)' : 'var(--gray-100)', color: c.isActive ? 'var(--green)' : 'var(--gray-400)' }}>{c.isActive ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <button className={styles.editBtn} onClick={() => startEdit(c)}>Edit</button>
                  <button className={styles.deleteBtn} onClick={() => setDeleteId(c.corporateCardId)}>Delete</button>
                </td>
              </tr>
            ))}
            {filteredCards.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--gray-400)' }}>{hasFilters ? 'No cards match filters.' : 'No cards registered yet.'}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <ConfirmDialog open={!!deleteId} title="Delete Card" message="Are you sure you want to remove this card? This action cannot be undone." confirmLabel="Delete" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  );
}
